import { FC, Fragment, ReactNode, useCallback, useMemo } from 'react';
import { Box, Divider, SxProps, Theme, Typography } from '@mui/material';
import {
	calcMonthlyMembershipTierAmount,
	findMembershipTierByFrequency,
	getPeriodFromFrequency,
	getTierFrequencyName,
	MembershipDto,
	MembershipTierDto,
	MembershipTierFrequencyEnum,
	priceFormatter,
} from '@sweatpals/shared';
import { Showable, CoreButtonBase, useStyles } from '@sweatpals/react-ui-kit';
import { isNumber } from 'lodash-es';
import { format } from 'date-fns';
import { membershipCardStyles } from './membership-card.styles';

export interface MembershipCardProps {
	hostPrimaryColorHex?: string;
	membership: MembershipDto;
	icon?: ReactNode;
	drawBackground?: boolean;
	sx?: SxProps<Theme>;
	onGetMembership?(tierId: string): void;
}

// eslint-disable-next-line complexity
export const MembershipCard: FC<MembershipCardProps> = (props) => {
	const { hostPrimaryColorHex, icon, drawBackground, sx, membership, onGetMembership } = props;

	const monthlyTier = useMemo(
		() => findMembershipTierByFrequency(membership.tiers, MembershipTierFrequencyEnum.Monthly),
		[membership.tiers]
	);

	const handleClickGet = useCallback((tier: MembershipTierDto) => {
		onGetMembership?.(tier.id);
	}, [onGetMembership]);

	const styles = useStyles(membershipCardStyles, hostPrimaryColorHex, drawBackground, sx);

	return (
		<Box sx={styles.root}>
			<Box sx={styles.content}>
				{icon && <Box sx={styles.iconBox}>{icon}</Box>}

				<Box sx={styles.header}>
					<Typography sx={styles.headerTitle}>{membership.name}</Typography>
					{membership.description && <Typography sx={styles.headerDescription}>{membership.description}</Typography>}
				</Box>

				<Box sx={styles.typesContainer}>
					{membership.tiers.map((tier, idx, arr) => {
						const monthlyAmount = calcMonthlyMembershipTierAmount(tier);
						const discountInPercentage = monthlyTier?.amount && isNumber(monthlyAmount) && monthlyTier.amount > monthlyAmount
							? Math.ceil(((monthlyTier.amount - monthlyAmount) / monthlyTier.amount) * 100)
							: 0;

						return (
							<Fragment key={tier.id}>
								<Box sx={styles.typeBox}>
									<Box sx={styles.typeInfo}>
										<Typography sx={styles.typeName}>
											{getTierFrequencyName(tier.frequency)}
										</Typography>
										{[
											MembershipTierFrequencyEnum.Annual,
											MembershipTierFrequencyEnum.OneTime
										].includes(tier.frequency) ? (
											<Box sx={styles.typePriceRow}>
												<Typography sx={styles.typePrice}>
													{priceFormatter(tier.amount)}
												</Typography>
											</Box>
										) : (
											<>
												<Box sx={styles.typePriceRow}>
													<Typography sx={styles.typePrice}>
														{priceFormatter(monthlyAmount)}
													</Typography>
													<Typography sx={styles.typePeriod}>/month</Typography>

													<Showable show={discountInPercentage}>
														<Box sx={styles.typeSavingLabel}>
															Save {discountInPercentage}%
														</Box>
													</Showable>
												</Box>

												<Showable
													hide={[
														MembershipTierFrequencyEnum.OneTime,
														MembershipTierFrequencyEnum.Monthly,
													].includes(tier.frequency)}
												>
													<Typography sx={styles.typeFullPrice}>
														Pay {priceFormatter(tier.amount)} for the {getPeriodFromFrequency(tier.frequency)}
													</Typography>
												</Showable>
											</>
										)}
										<Showable show={tier.signUpFee}>
											{() => (
												<Typography sx={styles.initialFee}>
													+ Initial fee {priceFormatter(tier.signUpFee)}
												</Typography>
											)}
										</Showable>
										<Showable show={!!tier.startDate && !!tier.endDate}>
											{() => (
												<Typography sx={styles.period}>
													{format(new Date(tier.startDate), 'd MMM yyyy')}
													{' - '}
													{format(new Date(tier.endDate), 'd MMM yyyy')}
												</Typography>
											)}
										</Showable>
									</Box>

									<CoreButtonBase sx={styles.typeGetButton} onClick={() => handleClickGet(tier)}>
										Get
									</CoreButtonBase>
								</Box>

								{idx !== arr.length - 1 && <Divider sx={styles.typeDivider} />}
							</Fragment>
						)
					})}
				</Box>
			</Box>
		</Box>
	);
};
